import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ResizeSensor } from 'css-element-queries';
import { extent, max } from 'd3-array';
import { Axis, axisBottom, AxisDomain, axisLeft } from 'd3-axis';
import { easeLinear } from 'd3-ease';
import { scaleLinear, scaleTime } from 'd3-scale';
import { select } from 'd3-selection';
import { area, line } from 'd3-shape';
import { timeFormat } from 'd3-time-format';
import 'd3-transition';
import { Subscription } from 'rxjs';

const textAnchor = 'text-anchor';
const strokeWidth = 'stroke-width';
const strokeDashArray = 'stroke-dasharray';
const lineExtra = 'line-extra';

export interface Rect {
    width?: any;
    height?: number;
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

export interface LineAreaOptions {
    thresholds?: LineAreaThreshold;
    lineStrokeColor?: string;
    sgLineStrokeColor?: string;
    tLineStrokeColor?: string;
    gLineStrokeColor?: string;
    graphLabel?: string;
    errorLabel?: string;
    dataLabel?: (d: any) => string;
    yTickLabel?: (d: any) => any; // d3 format function
    xTickLabel?: (d: any) => any; // d3 format function
    showLegend?: boolean;
}

export interface LineAreaThreshold {
    xAxis?: ThresholdLineConfig[];
    yAxis?: ThresholdLineConfig[];
}
export interface ThresholdLineConfig {
    label?: string;
    value: number;
    strokeColor: string;
    aboveColor?: string;
    belowColor?: string;
    active?: boolean;
}

export interface GraphPoint {
    x: any;
    y: any;
}

export interface BaseLineGraphData {
    mainData?: GraphPoint[];
    supergroupData?: GraphPoint[];
    targetData?: GraphPoint[];
    goalData?: GraphPoint[];
}

export interface GraphDefinition {
    data?: BaseLineGraphData;
    options?: LineAreaOptions;
    showSg?: boolean;
}

@Component({
    selector: 'old-ng-chart-line-area',
    template: ` <div #svgContainer></div> `,
    exportAs: 'ngChartLineArea',
})
export class OldLineAreaComponent
    implements OnChanges, OnDestroy, AfterViewInit
{
    @ViewChild('svgContainer') private readonly _svgContainer: ElementRef;
    @Input() graphSet: GraphDefinition[];
    @Input() customHeight: number;
    @Input() customWidth: number;
    @Input() graphSetSGToggles: boolean[];
    @Input() isVariance: boolean;

    // default dimensions
    height = 350;
    width = 640;
    top = 20;
    right = 40;
    bottom = 20;
    left = 40;

    _options: LineAreaOptions;
    renderedValue = false;
    defaultFormatdtg: any = timeFormat('%b %d');

    allSgPaths: any[];
    gc: any; // graph config
    resizelistener: any;

    rect: Rect;
    initializedDOM = false;

    @Input()
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get rendered() {
        return this.renderedValue;
    }

    set rendered(val) {
        this.renderedValue = val;
        this.renderedChange.emit(this.renderedValue);
    }

    @Output() renderedChange = new EventEmitter();

    private _eventSub: Subscription;

    svg: any;

    constructor(
        private readonly _el: ElementRef,
        private readonly cdRef: ChangeDetectorRef,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngAfterViewInit() {
        const d = this._el.nativeElement.getBoundingClientRect();

        this.setRect({
            width: d.width || this.width,
            height: this.customHeight || this.height,
            top: this.top,
            right: this.right,
            bottom: this.bottom,
            left: d.left || this.left,
        });

        this.initializedDOM = true;

        if (this.graphSet.length) {
            this.renderContainer(this.rect);
            this._render(this.graphSet);
        }

        this.resizelistener = new ResizeSensor(
            this._el.nativeElement,
            (dimensions) => {
                this.setRect({
                    height: this.customHeight || dimensions.height,
                    width: this.customWidth || dimensions.width,
                    bottom: this.bottom,
                });
                this.renderContainer(this.rect);
                this._render(this.graphSet);
            },
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line no-prototype-builtins
        if (
            this.initializedDOM &&
            // eslint-disable-next-line no-prototype-builtins
            changes.hasOwnProperty('graphSet') &&
            this.graphSet.length
        ) {
            this.renderContainer(this.rect);
            this._render(this.graphSet);
        }

        // eslint-disable-next-line no-prototype-builtins
        if (changes.hasOwnProperty('graphSetSGToggles')) {
            this.toggleSG();
        }
    }

    ngOnDestroy(): void {
        if (this._eventSub) {
            this._eventSub.unsubscribe();
        }
        if (this.resizelistener) {
            this.resizelistener.detach();
            this.resizelistener = null;
        }

        this.allSgPaths = null;
        this.gc = null;
        this.rect = null;
        this._options = null;
        this._eventSub = null;
    }

    private renderContainer(rect: Rect): any {
        let container: any;

        // empty the graph only on the first (main) metric
        const svgcontdiv = select(this._svgContainer.nativeElement);
        const graphcont = svgcontdiv.select('.main-oa-variance-graph');

        if (graphcont.empty()) {
            container = svgcontdiv.append('svg');

            container
                .attr('class', 'main-oa-variance-graph')
                .attr('xmlns', 'http://www.w3.org/2000/svg')
                .attr('version', '1.1')
                .attr('tabindex', '-1')
                .attr('width', rect.width - 5)
                .attr('height', rect.height + rect.top + rect.bottom);
        } else {
            graphcont
                .attr('width', rect.width - 5)
                .attr('height', rect.height + rect.top + rect.bottom);
            container = graphcont;
        }

        // SCALE BY GIVEN DIMENSIONS
        const x = scaleTime().range([0, rect.width - rect.left - rect.right]);
        const y = scaleLinear().range([rect.height, 0]);
        this.gc = { x, y, container };

        // ADD MAIN GRAPH (one per metric)
        this.gc.container.select('g').remove();
        this.svg = this.gc.container
            .append('g')
            .attr(
                'transform',
                `translate(${this.rect.left}, ${this.rect.top})`,
            );
    }

    private getDomains(graphSet: GraphDefinition[]): any {
        let extentdata = [];

        graphSet.forEach((g) => {
            if (g.data) {
                const pdata = g.data.mainData;
                extentdata = [...extentdata, ...pdata];
                if (g.data.supergroupData)
                    extentdata = [...extentdata, ...g.data.supergroupData];
                if (g.data.targetData)
                    extentdata = [...extentdata, ...g.data.targetData];
                if (g.data.goalData)
                    extentdata = [...extentdata, ...g.data.goalData];
            }
        });

        return this.setDomains(extentdata);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private setDomains(extentdata) {
        let xdomain;
        let ydomain;

        if (this.isVariance) {
            // variance
            xdomain = extent(extentdata, function (d: any) {
                return d.x;
            });
            ydomain = [
                max(extentdata, function (d: any) {
                    return Math.abs(d.y != null ? d.y : 1);
                }) * -1,
                max(extentdata, function (d: any) {
                    return Math.abs(d.y != null ? d.y : 1);
                }),
            ];
        } else {
            // trend
            xdomain = extent(extentdata, function (d: any) {
                return d.x;
            });
            ydomain = extent(extentdata, function (d: any) {
                return d.y;
            });
        }
        return {
            xdomain,
            ydomain,
        };
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private setRect(rect?: Rect) {
        if (!rect) {
            rect = {
                width: this.width,
                height: this.customHeight || this.height,
                top: this.top,
                right: this.right,
                bottom: this.bottom,
                left: this.left,
            };
        }

        this.rect = {
            top: this.top,
            right: this.right,
            bottom: this.bottom,
            left: this.left,
            ...rect,
        };
    }

    private _render(g: GraphDefinition[]): void {
        if (!g || !g.length || !g[0].data) return;

        this.rendered = false;

        const domains = this.getDomains(g);
        this.allSgPaths = [];

        g.forEach((definition: GraphDefinition, idx: number) => {
            this.__renderGraphDef(definition, domains, idx, g.length);
        });

        this.rendered = true;

        this.cdRef.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private __renderGraphDef(
        definition,
        domains,
        idx: number,
        glength: number,
    ) {
        const data = definition.data;
        const options = definition.options;

        const performerData: GraphPoint[] = data.mainData;
        let supergroupData: GraphPoint[];
        let targetData: GraphPoint[];
        let goalData: GraphPoint[];

        let clipPaths: any[];

        if (data.supergroupData) {
            supergroupData = data.supergroupData;
        }
        if (data.targetData) {
            targetData = data.targetData;
        }
        if (data.goalData) {
            goalData = data.goalData;
        }

        if (!options) return;

        // TODO: remove
        if (!options.thresholds) {
            return;
        }

        const thresholdsX: ThresholdLineConfig[] =
            options.thresholds.xAxis || [];
        const thresholdsY: ThresholdLineConfig[] =
            options.thresholds.yAxis || [];

        // POSITION AXIS LABELS
        const xAxis = axisBottom(this.gc.x);
        const yAxis = axisLeft(this.gc.y);

        // Primary Lines
        const mainline = area()
            .x((d: any) => this.gc.x(d.x))
            .y((d: any) => this.gc.y(d.y));
        // .curve(d3.curveBasis);

        const sgline = line()
            .x((d: any) => this.gc.x(d.x))
            .y((d: any) => this.gc.y(d.y));

        const tline = line()
            .x((d: any) => this.gc.x(d.x))
            .y((d: any) => this.gc.y(d.y));

        const gline = line()
            .x((d: any) => this.gc.x(d.x))
            .y((d: any) => this.gc.y(d.y));

        const area1 = area()
            .x((d: any) => this.gc.x(d.x))
            .y1((d: any) => this.gc.y(d.y));

        const thresholdXScale = this.gc.x.domain(domains.xdomain);
        const thresholdYScale = this.gc.y.domain(domains.ydomain).nice();

        this.svg.datum(performerData);

        if (this.isVariance) {
            this.svg
                .append('clipPath')
                .attr('id', 'clip-above')
                .append('path')
                .attr('d', area1.y0(this.rect.height));

            this.svg
                .append('clipPath')
                .attr('id', 'clip-below')
                .append('path')
                .attr('d', area1.y0(0));

            // ADD SHADED AREAS BY Y-AXIS THRESHOLDS
            if (this.isVariance) {
                clipPaths = [];
                this.addShadedAreasThresholdY(thresholdsY, clipPaths, area1);
            }
        }

        // the performer line
        const { performerPath, tPath, gPath } = this.addPaths({
            targetData,
            supergroupData,
            goalData,
            mainline,
            tline,
            sgline,
            gline,
            options,
        });

        if (idx === glength - 1) {
            this.appendGridLines();
        }

        if (idx === glength - 1) {
            this.appendAxes(xAxis, yAxis, options);
        }

        // LABEL OF GRAPH
        if (options.graphLabel) {
            this.addGraphLabel(options);
        }

        this.addHorizontalPaths(
            thresholdsY,
            thresholdXScale,
            thresholdYScale,
            domains,
        );

        // ADD VERTICAL DOTTED THRESHOLD LINES
        if (idx === glength - 1) {
            // only with last graph def
            this.addVerticalPaths(thresholdsX, thresholdXScale);
        }

        this.appendDataPoints(
            { performerData, targetData, supergroupData, goalData },
            idx,
            options,
        );

        if (this.rendered === false) {
            this.animatePaths(performerPath, tPath, gPath, clipPaths, idx);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addPaths({
        targetData,
        supergroupData,
        goalData,
        mainline,
        tline,
        sgline,
        gline,
        options,
    }) {
        // the performer line
        let performerPath: any = null;
        // the SG Average line
        let sgPath: any = null;
        // the Target line
        let tPath: any = null;
        // the Goal line
        let gPath: any = null;

        // ADD THE LINE GRAPH
        // performer line
        performerPath = this.svg
            .append('path')
            .attr('class', 'line')
            .style('stroke', options.lineStrokeColor || '#000')
            .style(strokeWidth, '2')
            .attr('d', mainline);

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (supergroupData && supergroupData.length) {
            // supergroup average line
            sgPath = this.svg.append('path');
            sgPath
                .datum(supergroupData)
                .attr('class', lineExtra)
                .style('fill', 'none')
                .style('stroke', options.sgLineStrokeColor || '#000')
                .style('display', 'none')
                .style('opacity', 0.7)
                .attr('d', sgline);
            this.allSgPaths.push(sgPath);
        }

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (targetData && targetData.length) {
            // target line
            tPath = this.svg.append('path');
            tPath
                .datum(targetData)
                .attr('class', lineExtra)
                .style('fill', 'none')
                .style('stroke', options.tLineStrokeColor || '#000')
                .style(strokeDashArray, '7')
                .style(strokeWidth, '4px')
                .style('opacity', 0.9)
                .attr('d', tline);
        }

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (goalData && goalData.length) {
            // goal line
            gPath = this.svg.append('path');
            gPath
                .datum(goalData)
                .attr('class', lineExtra)
                .style('fill', 'none')
                .style('stroke', options.gLineStrokeColor || '#000')
                .style('opacity', 0.85)
                .style(strokeWidth, '2px')
                .attr('d', gline);
        }

        return {
            performerPath,
            tPath,
            gPath,
            sgPath,
        };
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    animatePaths(performerPath, tPath, gPath, clipPaths, idx) {
        let d = 500;

        const pathNodes = [
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            {
                path: performerPath,
                length: performerPath.node().getTotalLength(),
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                dots: `.data-circle${idx}`,
            },
        ];

        if (gPath) {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            pathNodes.push({
                path: gPath,
                length: gPath.node().getTotalLength(),
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                dots: `.goal-circle${idx}`,
            });
        }

        pathNodes.forEach((p) => {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            p.path
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                .attr(strokeDashArray, `${p.length} ${p.length}`)
                .attr('stroke-dashoffset', -p.length)
                .transition()
                .on('end', () => {
                    this.svg.selectAll(p.dots).style('display', 'block');
                })
                .delay(d)
                .duration(1500)
                .ease(easeLinear)
                .attr('stroke-dashoffset', 0);

            d += 500;
        });

        // animate the target path separately
        if (tPath) {
            d += 500;
            setTimeout(() => {
                tPath.style('display', 'block');
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                this.svg
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    .selectAll(`.target-circle${idx}`)
                    .style('display', 'block');
            }, d);
        }

        // if in variance, show the shaded areas
        if (this.isVariance) {
            setTimeout(() => {
                clipPaths.forEach((cp) => {
                    cp.style('opacity', 0.9)
                        .style('transform', 'translateX(0rem)')
                        .style('transition', '0.8s ease-in-out');
                });
            }, d - 200);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addGraphLabel(ops) {
        this.svg
            .append('text')
            .attr('x', this.rect.width / 2)
            .attr('y', this.rect.height + this.rect.bottom - 5)
            .attr(textAnchor, 'middle')
            .style('font-size', '14px')
            .attr('class', 'shadow')
            .text(ops.graphLabel);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addShadedAreasThresholdY(thresholdsY, clipPaths, area1) {
        thresholdsY.forEach((d) => {
            clipPaths.push(
                this.svg
                    .append('path')
                    .attr('class', 'area below')
                    .style('fill', d.belowColor || 'transparent')
                    .style('opacity', 0)
                    .style('transform', 'translateX(-0.5rem)')
                    .attr('clip-path', 'url(#clip-below)')
                    .attr('d', area1.y0(this.gc.y(d.value))),
            );

            clipPaths.push(
                this.svg
                    .append('path')
                    .attr('class', 'area above')
                    .style('fill', d.aboveColor || 'transparent')
                    .style('opacity', 0)
                    .style('transform', 'translateX(-0.5rem)')
                    .attr('clip-path', 'url(#clip-above)')
                    .attr('d', area1),
            );
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addHorizontalPaths(thresholdsY, thresholdXScale, thresholdYScale, domains) {
        // ADD HORIZONTAL DOTTED THRESHOLD LINES
        thresholdsY.forEach((d: any) => {
            this.svg
                .append('line')
                .style('stroke', d.strokeColor)
                .style(strokeWidth, '2px')
                .style(strokeDashArray, '5 5')
                .attr('x1', thresholdXScale(domains.xdomain[0]))
                .attr('y1', thresholdYScale(d.value))
                .attr('x2', thresholdXScale(domains.xdomain[1]))
                .attr('y2', thresholdYScale(d.value))
                .attr('class', 'zeroline-y');
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addVerticalPaths(thresholdsX, thresholdXScale) {
        // ADD VERTICAL DOTTED THRESHOLD LINES
        thresholdsX.forEach((d: any) => {
            this.svg
                .append('line')
                .style('stroke', d.strokeColor)
                .style(strokeWidth, '2px')
                .style(strokeDashArray, '3')
                .style('opacity', 0.5)
                .attr('x1', thresholdXScale(d.value))
                .attr('y1', 0)
                .attr('x2', thresholdXScale(d.value))
                .attr('y2', this.rect.height)
                .attr('class', 'zeroline-x');
        });
    }

    // gridlines in x axis function
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    makeXGridlines(x) {
        return axisBottom(x);
    }

    // gridlines in y axis function
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    makeYGridlines(y) {
        return axisLeft(y);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    defaultDataLabel(data: any) {
        const date = this.defaultFormatdtg(data.x);
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        return `${date}: ${data.y}`;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    defaultYTickLabel() {
        return '';
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    defaultXTickLabel() {
        return '';
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    appendGridLines() {
        // add vertical gridlines
        this.svg
            .append('g')
            .attr('class', 'gridX')
            .attr('stroke-opacity', '0.1')
            .attr('transform', `translate(0, ${this.rect.height})`)
            .call(
                this.makeXGridlines(this.gc.x)
                    .tickSize(-this.rect.height)
                    .tickFormat(() => ''),
            );

        // add horizontal gridlines
        this.svg
            .append('g')
            .attr('class', 'gridY')
            .attr('stroke-opacity', '0.1')
            .call(
                this.makeYGridlines(this.gc.y)
                    .tickSize(-this.rect.width)
                    .tickFormat(() => ''),
            );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    appendAxes(
        xAxis: Axis<AxisDomain>,
        yAxis: Axis<AxisDomain>,
        options: LineAreaOptions,
    ) {
        this.svg
            .append('g') // x axis
            .attr('class', 'x axis')
            .attr('transform', `translate(0, ${this.rect.height})`)
            .call(
                xAxis.tickFormat(
                    options.xTickLabel
                        ? options.xTickLabel
                        : this.defaultXTickLabel,
                ),
            );

        this.svg
            .append('g') // y axis
            .attr('class', 'y axis')
            .call(
                yAxis.tickFormat(
                    options.yTickLabel
                        ? options.yTickLabel
                        : this.defaultYTickLabel,
                ),
            )
            .append('text')
            .attr('transform', `translate(5, ${this.rect.height}) rotate(-90)`)
            .attr('dy', '.71em')
            .style(textAnchor, 'start')
            .attr('class', 'shadow');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    appendDataPoints(dotsForData: any, idx: number, options: LineAreaOptions) {
        // ADD DATA POINT CIRCLES AND THEIR HOVER EVENTS
        for (const prop in dotsForData) {
            const dataset = dotsForData[prop];
            if (!dataset) continue;

            let fillcolor = '';
            let circleclass = '';

            switch (prop) {
                case 'performerData':
                    fillcolor = options.lineStrokeColor || '#000';
                    circleclass = `data-circle${idx}`;
                    break;
                case 'targetData':
                    fillcolor = options.tLineStrokeColor || '#000';
                    circleclass = `target-circle${idx}`;
                    break;
                case 'goalData':
                    fillcolor = options.gLineStrokeColor || '#000';
                    circleclass = `goal-circle${idx}`;
                    break;
                default:
                    fillcolor = options.sgLineStrokeColor || '#000';
                    circleclass = `sg-circle${idx}`;
                    break;
            }

            const opacity = prop === 'performerData' ? 0.9 : 0.6;

            this.svg
                .selectAll('line-circle')
                .data(dataset)
                .enter()
                .append('circle')
                .attr('class', circleclass)
                .attr('fill', fillcolor)
                .attr('stroke', 'none')
                .attr('opacity', opacity)
                .attr('r', 2)
                .attr('cx', (d) => {
                    return this.gc.x(d.x);
                })
                .attr('cy', (d) => {
                    return this.gc.y(d.y);
                })
                .style('display', 'none')
                .on('mouseover', (a, b, c) => {
                    this.handleMouseOver(a, b, c, {
                        datapointrect,
                        datapointlabel,
                        dataset,
                        options,
                        fillcolor,
                    });
                })
                .on('mouseout', (a, b, c) => {
                    this.handleMouseOut(
                        a,
                        b,
                        c,
                        datapointrect,
                        datapointlabel,
                        fillcolor,
                    );
                });
        }

        const datapointrect = this.svg
            .append('rect')
            .attr('width', '100px')
            .attr('height', '15px')
            .attr('rx', '2')
            .attr('ry', '2')
            .style('opacity', 0.9)
            .style('display', 'none');

        const datapointlabel = this.svg
            .append('text')
            .attr('class', 'point-label')
            .attr(textAnchor, 'middle')
            .style('display', 'none')
            .style('font-size', '11px');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    handleMouseOut(a, b, c, datapointrect, datapointlabel, fillcolor) {
        const el = c[b];
        select(el)
            .transition()
            .attr('r', 2)
            .attr('fill', fillcolor)
            .attr('stroke', 'none')
            .attr(strokeWidth, null);

        datapointlabel.style('display', 'none');
        datapointrect.style('display', 'none');
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    handleMouseOver(
        a,
        b,
        c,
        { datapointrect, datapointlabel, dataset, options, fillcolor },
    ) {
        const el = c[b];
        let left = Number(select(el).attr('cx'));
        let top = Number(select(el).attr('cy'));

        if (left + 90 > this.rect.width) {
            left -= 50;
        } else if (left - 90 < 0) {
            left += 50;
        }

        if (top + 20 >= this.rect.height + this.rect.top) {
            top -= 5;
        } else if (top - 10 < 0) {
            top += 20;
        } else {
            top -= 15;
        }

        const d = dataset[b];

        select(el)
            .transition()
            .attr('r', 5)
            .attr('fill', 'transparent')
            .attr('stroke', fillcolor)
            .attr(strokeWidth, 3);

        datapointrect
            .style('display', 'block')
            .attr('x', left)
            .attr('y', top)
            .style('transform', 'translate(-6.1%, -0.8rem)')
            .style('fill', '#ffffff');

        datapointlabel
            .style('display', 'block')
            .attr('x', left)
            .attr('y', top)
            .attr('stroke-color', '#000')

            .text(
                options.dataLabel
                    ? options.dataLabel(d)
                    : this.defaultDataLabel(d),
            );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    toggleSG() {
        if (this.allSgPaths && this.graphSetSGToggles) {
            this.graphSetSGToggles.forEach((p, idx) => {
                const sgPath = this.allSgPaths[idx];

                if (sgPath) {
                    if (p === true) {
                        if (sgPath.style('display') === 'none') {
                            sgPath.style('display', 'block');
                            this.svg
                                .selectAll(`.sg-circle${idx}`)
                                .style('display', 'block');
                        }
                    } else {
                        sgPath.style('display', 'none');
                        this.svg
                            .selectAll(`.sg-circle${idx}`)
                            .style('display', 'none');
                    }
                }
            });
        }
    }
}
